/** Public game index and individual universal-link landing pages. */
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Download, Gamepad2 } from "lucide-react";
import { gameCatalog } from "./audienceContent";
import { CTA, GameArt, GamesSection } from "./MarketingExperience";

export default function Play() {
  const { game } = useParams();
  const selected = gameCatalog.find((item) => item.id === game);
  useEffect(() => {
    document.title = `${selected?.name || "Games & KwK Play"} | KwK`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        selected?.detail ||
          "Explore five private single-player games in KwK Play. Download only the games you choose; sponsored rewards have separate eligibility and terms.",
      );
  }, [selected]);
  if (game && !selected)
    return (
      <main id="main-content" className="mk-site mk-not-found">
        <h1>Let’s find your next game.</h1>
        <p>This game link is unavailable.</p>
        <CTA to="/play">Browse KwK Play</CTA>
      </main>
    );
  return (
    <main id="main-content" className="mk-site">
      {selected ? (
        <section className="mk-game-page">
          <div className="mk-container mk-split">
            <div>
              <span className="mk-eyebrow">
                KWK PLAY · {selected.genre.toUpperCase()}
              </span>
              <h1>{selected.name}</h1>
              <p>{selected.detail}</p>
              <div className="mk-actions">
                <a className="mk-button" href={`kwk://play/${selected.id}`}>
                  Open in KwK <ArrowUpRight size={18} />
                </a>
                <CTA secondary to="/play">
                  All games
                </CTA>
              </div>
              <p className="mk-fine">
                Requires KwK installed and an eligible signed-in role. Download
                and play are separate actions in the app. If the app doesn’t
                open, use the contact option below for setup help.
              </p>
              <Link
                className="mk-text-link"
                to="/#join"
                state={{ role: "buyer" }}
              >
                Ask about getting KwK
              </Link>
            </div>
            <GameArt game={selected} />
          </div>
        </section>
      ) : (
        <section className="mk-game-page">
          <div className="mk-container mk-game-intro">
            <span className="mk-eyebrow">
              MEET YOUR BETWEEN-ERRANDS HAPPY PLACE
            </span>
            <h1>
              Life has a lot of lists.
              <br />
              This part is for play.
            </h1>
            <p>
              A constellation to build. A pantry to remember. A neighborhood to
              explore. Five private single-player games, right inside your KwK
              launcher.
            </p>
            <div className="mk-actions" style={{ justifyContent: "center" }}>
              <CTA to="#games">Find your next favorite</CTA>
              <CTA secondary to="/rewards">
                How points are different
              </CTA>
            </div>
          </div>
        </section>
      )}
      <GamesSection full />
      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-benefits">
            <article>
              <Download size={28} />
              <h3 style={{ marginTop: 24 }}>Your library. Your choice.</h3>
              <p>
                Games are separate downloads. Open a game page, review it, and
                choose what to install. Your current app shows progress and
                available updates.
              </p>
            </article>
            <article>
              <ShieldCheck size={28} />
              <h3 style={{ marginTop: 24 }}>Your own little world.</h3>
              <p>
                Current games keep play private. Personal progress, daily
                challenges and available replay controls are not a public
                leaderboard or a multiplayer promise.
              </p>
            </article>
            <article>
              <Gamepad2 size={28} />
              <h3 style={{ marginTop: 24 }}>Fun first. Clear rewards.</h3>
              <p>
                A score, a boost or a completed practice board is not money.
                Eligible sponsored game offers explain their objective, provider
                and expiration separately. No cash-out is promised.
              </p>
            </article>
            <article>
              <ShieldCheck size={28} />
              <h3 style={{ marginTop: 24 }}>Points, without the confusion.</h3>
              <p>
                The expanded Universal KwK Points and Seller Points program is
                in rollout. Rewards Lab is test-only. Check the app for actual
                live offers and confirmed balances.
              </p>
              <div className="mk-actions">
                <CTA secondary to="/rewards">
                  Meet KwK Points
                </CTA>
              </div>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}
